import { 
  MessageSquare, 
  Lock, 
  CheckCircle2, 
  AlertCircle, 
  UserPlus, 
  Clock,
  FileText,
  Paperclip,
  Download,
  User
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TimelineInteraction {
  id: number;
  ticket_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_type: 'client' | 'internal';
  interaction_type: 'comment' | 'internal_note' | 'status_change' | 'assignment' | 'solution' | 'validation' | 'info_request' | 'created';
  message: string;
  metadata?: {
    time_spent?: number;
    old_status?: string;
    new_status?: string;
    resolved?: boolean;
    assigned_to?: number;
    assigned_to_name?: string;
  };
  is_internal: boolean;
  created_at: string;
  attachments?: Array<{
    id: number;
    filename: string;
    original_filename: string;
    file_size: number;
    mime_type: string;
    created_at: string;
  }>;
}

interface TicketTimelineProps {
  interactions: TimelineInteraction[];
  currentUserType?: 'client' | 'internal';
  onDownloadAttachment?: (attachmentId: number) => void;
}

const INTERACTION_CONFIG = {
  created: {
    icon: FileText,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-500/10',
    label: 'Ticket Criado'
  },
  comment: {
    icon: MessageSquare,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-500/10',
    label: 'Comentário'
  },
  internal_note: {
    icon: Lock,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-500/10',
    label: 'Nota Interna'
  },
  solution: {
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-500/10',
    label: 'Solução Proposta'
  },
  info_request: {
    icon: AlertCircle,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-500/10',
    label: 'Informações Solicitadas'
  },
  validation: {
    icon: CheckCircle2,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-500/10',
    label: 'Validação'
  },
  assignment: {
    icon: UserPlus,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-500/10',
    label: 'Atribuição'
  },
  status_change: {
    icon: Clock,
    color: 'text-slate-600 dark:text-slate-400',
    bgColor: 'bg-slate-500/10',
    label: 'Mudança de Status'
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora mesmo';
  if (diffMins < 60) return `Há ${diffMins} min`;
  if (diffHours < 24) return `Há ${diffHours}h`;
  if (diffDays < 7) return `Há ${diffDays} dias`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export function TicketTimeline({ 
  interactions, 
  currentUserType = 'client',
  onDownloadAttachment 
}: TicketTimelineProps) {
  // Filtrar notas internas para clientes
  const visibleInteractions = interactions.filter(interaction => {
    if (currentUserType === 'client' && interaction.is_internal) {
      return false;
    }
    return true;
  });

  if (visibleInteractions.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Nenhuma interação ainda</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {visibleInteractions.map((interaction, index) => {
        const config = INTERACTION_CONFIG[interaction.interaction_type];
        const Icon = config.icon;
        const isLastItem = index === visibleInteractions.length - 1;

        return (
          <div key={interaction.id} className="flex gap-4">
            {/* Timeline Line */}
            <div className="flex flex-col items-center">
              <div className={cn(
                "h-10 w-10 rounded-full flex items-center justify-center",
                config.bgColor
              )}>
                <Icon className={cn("h-5 w-5", config.color)} />
              </div>
              {!isLastItem && (
                <div className="w-px h-full bg-border mt-2" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pb-6">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">
                      {interaction.user_name}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {config.label}
                    </Badge>
                    {interaction.is_internal && (
                      <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
                        <Lock className="h-3 w-3 mr-1" />
                        Interno
                      </Badge>
                    )}
                    {interaction.user_type === 'internal' && (
                      <Badge variant="outline" className="text-xs border-blue-500 text-blue-600">
                        Equipe
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(interaction.created_at)}
                  </p>
                </div>

                {/* Time Spent Badge */}
                {interaction.metadata?.time_spent && interaction.metadata.time_spent > 0 && (
                  <Badge variant="outline" className="text-xs">
                    <Clock className="h-3 w-3 mr-1" />
                    {interaction.metadata.time_spent} min
                  </Badge>
                )}
              </div>

              {/* Message */}
              {interaction.message && (
                <div className={cn(
                  "text-sm whitespace-pre-wrap rounded-lg p-3 mt-2",
                  interaction.is_internal 
                    ? "bg-amber-50 dark:bg-amber-950/20 text-amber-900 dark:text-amber-100 border border-amber-200 dark:border-amber-800"
                    : "bg-muted/50"
                )}>
                  {interaction.message}
                </div>
              )}

              {/* Status Change Info */}
              {interaction.interaction_type === 'status_change' && interaction.metadata && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <Badge variant="outline">{interaction.metadata.old_status}</Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="outline">{interaction.metadata.new_status}</Badge>
                </div>
              )}

              {/* Assignment Info */}
              {interaction.interaction_type === 'assignment' && interaction.metadata?.assigned_to_name && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    Atribuído para: <strong>{interaction.metadata.assigned_to_name}</strong>
                  </span>
                </div>
              )}

              {/* Validation Result */}
              {interaction.interaction_type === 'validation' && interaction.metadata && (
                <div className="mt-2">
                  {interaction.metadata.resolved ? (
                    <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Problema Resolvido
                    </Badge>
                  ) : (
                    <Badge className="bg-orange-500/20 text-orange-700 dark:text-orange-400">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Ainda há problemas
                    </Badge>
                  )}
                </div>
              )}

              {/* Attachments */}
              {interaction.attachments && interaction.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Paperclip className="h-3 w-3" />
                    <span>{interaction.attachments.length} anexo(s)</span>
                  </div>
                  {interaction.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-2 border rounded-lg bg-background hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate">
                          {attachment.original_filename}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.file_size)}
                        </p>
                      </div>
                      {onDownloadAttachment && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDownloadAttachment(attachment.id)}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
